import { useWorkOrderItemHistory } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.hooks';
import {
  WorkOrderItemHistory,
  WorkOrderItemHistoryRequest,
} from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { COLUMNS_WIDTH, getTableScrollX } from '@/utils/table';
import {
  Col,
  Divider,
  Empty,
  Modal,
  ModalProps,
  Row,
  Statistic,
  Table,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import { useWorkorderDetailContext } from '../../../WorkorderDetailProvider';
import { WorkorderItem } from '../../WorkOrderItemModal';
import formatterPrice from '@/utils/number/formatterPrice';

interface Props extends ModalProps {
  params?: WorkOrderItemHistoryRequest;
  itemPart: WorkorderItem;
}

const WorkOrderItemHistoryListModal: React.FC<Props> = ({
  params,
  itemPart,
  ...props
}) => {
  const { data, isLoading } = useWorkOrderItemHistory(params);

  const {
    workorder: { data: workorder },
  } = useWorkorderDetailContext();

  const workorderList = data?.data.filter((item) => !!item.workOrderId);
  const saleList = data?.data.filter((item) => !!item.saleId);

  const columnsDefault: ColumnsType<WorkOrderItemHistory> = [
    {
      key: 'customer',
      dataIndex: 'customer',
      title: 'Cliente',
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
    },
    {
      key: 'quantity',
      dataIndex: 'quantity',
      title: 'Quantidade',
      width: COLUMNS_WIDTH.small,
      ellipsis: true,
      align: 'right',
    },
    {
      key: 'unitPrice',
      dataIndex: 'unitPrice',
      title: 'Valor unitário (R$)',
      width: COLUMNS_WIDTH.price,
      ellipsis: true,
      align: 'right',
      render: (value: number) => formatterPrice(value),
    },
  ];

  const workorderColumns: ColumnsType<WorkOrderItemHistory> = [
    {
      key: 'workOrderNumber',
      dataIndex: 'workOrderNumber',
      title: 'Orçamento',
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
    },
    {
      key: 'workOrderDate',
      dataIndex: 'workOrderDate',
      title: 'Data do orçamento',
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
      render: (value) => (!!value ? moment(value).format('DD/MM/YYYY') : '-'),
    },
  ];

  const saleColumns: ColumnsType<WorkOrderItemHistory> = [
    {
      key: 'saleIdd',
      dataIndex: 'saleIdd',
      title: 'Venda direta',
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
    },
    {
      key: 'workOrderDate',
      dataIndex: 'workOrderDate',
      title: 'Data da venda',
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
    },
  ];

  const workorderColumnsConcat = workorderColumns.concat(columnsDefault);
  const saleColumnsConcat = saleColumns.concat(columnsDefault);

  return (
    <Modal
      {...props}
      title="Histórico das vendas direta e do orçamento"
      width={800}
      footer={null}
    >
      <Typography.Title level={3} style={{ margin: 0 }}>
        {itemPart.itemName}
      </Typography.Title>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24} md={12} style={{ gap: 10, display: 'flex' }}>
          <span style={{ whiteSpace: 'nowrap' }}>Código do fabricante:</span>
          <span>{itemPart.manufacturingCode}</span>
        </Col>

        <Col span={24} md={12} style={{ gap: 10, display: 'flex' }}>
          <span style={{ whiteSpace: 'nowrap' }}>Cliente da OS:</span>
          <span>{workorder?.customer?.name}</span>
        </Col>
        <Col span={24} style={{ gap: 10, display: 'flex' }}>
          <span style={{ whiteSpace: 'nowrap' }}>Intervalo da pesquisa:</span>
          <span>
            {`${moment(params?.from).format('DD/MM/YYYY')} até ${moment(
              params?.to,
            ).format('DD/MM/YYYY')}`}
          </span>
        </Col>
        <Col span={24} style={{ gap: 10, display: 'flex' }}>
          <span style={{ whiteSpace: 'nowrap' }}>Filtros:</span>
          <span>
            {`${
              !params?.customerId ? 'Todos os clientes' : 'Cliente da OS'
            }, OSs fechadas e Vendas autorizadas`}
          </span>
        </Col>
      </Row>

      {!workorderList?.length && !saleList?.length && (
        <>
          <Divider />
          <Empty description="Não há dados" />
        </>
      )}

      {!!workorderList?.length && (
        <>
          <Divider />

          <Typography.Title level={5}>Orçamento</Typography.Title>
          <Table
            dataSource={workorderList}
            columns={workorderColumnsConcat}
            pagination={false}
            scroll={{ x: getTableScrollX(workorderColumnsConcat) }}
            loading={isLoading}
          />
        </>
      )}

      {!!saleList?.length && (
        <>
          <Divider />

          <Typography.Title level={5}>Venda direta</Typography.Title>
          <Table
            dataSource={saleList}
            columns={saleColumnsConcat}
            pagination={false}
            scroll={{ x: getTableScrollX(saleColumnsConcat) }}
            loading={isLoading}
          />
        </>
      )}

      <Divider />

      <Statistic
        title="Total"
        value={formatterPrice(
          data?.data.reduce((acc, item) => acc + item.unitPrice, 0) || 0,
        )}
      />
    </Modal>
  );
};

export default WorkOrderItemHistoryListModal;
